function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import * as React from 'react';
import partition from 'lodash/partition';
import camelCase from 'lodash/camelCase';
import { clsx } from 'clsx';
import { useTranslation } from 'common/i18n/hooks';
import { isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
import { SelectTheme } from 'common/components/select/constants';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { SectionItem } from 'common/components/items-menu/types';
import { ButtonSize } from 'common/components/button/constants';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { isXauMathType } from 'common/query-builder/query-builder/util';
import { BEHAVIOR_PROPERTIES } from 'common/query-builder/query-block/properties-menu/constants';
import { SelectReact } from 'common/components/select';

// eslint-disable-next-line import/no-cycle
import { QueryEntrySectionReact } from '../index';
import { Entry, EntryPart, EntrySectionUpdates, FiltersOperator, FiltersOperatorDisplayLabels, QueryEntrySectionProps, QueryFilterEntry, QuerySuperEntry, RowLabelType, SelectorTypeDefinitions } from '../types';
import { getFilterEntriesFromFilters } from '../row-types/filter/utils';

/**
 * @todo [pablo] all of this stuff re: 1st time filters can be simplified by using a one-off filter div since
 * 1st time filters are not actionable in any way other than removing
 */
export function RowFilters(props: {
  entry: QuerySuperEntry;
  index: number;
  onUpdate: (rawFilters?: PropertyFilter[], filtersOperator?: FiltersOperator, queryReason?: QueryReason, updates?: EntrySectionUpdates<QueryFilterEntry>) => void;
  filtersOperators?: FiltersOperator[];
  selectedFiltersOperator?: FiltersOperator;
  eventNames: string[];
  filters: PropertyFilter[];
  sectionParams?: Partial<QueryEntrySectionProps>;
  extraClass?: Record<string, boolean>;
  hideEvents: boolean;
  hasNewUI: boolean;
  nestedSectionParams: Partial<QueryEntrySectionProps>;
  placeholders: [EntryPart, Entry];
  sectionProps: QueryEntrySectionProps;
  onAbortStage: () => void;
  metricLevelDataGroups?: boolean;
}) {
  var _props$placeholders, _props$placeholders2;
  const showPlaceholder = ((_props$placeholders = props.placeholders) === null || _props$placeholders === void 0 ? void 0 : _props$placeholders.includes(EntryPart.Filters)) || ((_props$placeholders2 = props.placeholders) === null || _props$placeholders2 === void 0 ? void 0 : _props$placeholders2.includes(EntryPart.FirstTimeFilters));
  const filtersOperator = props.entry.filtersOperator;
  const aggregationOperator = props.entry.aggregationOperator;
  const selectedFiltersOperator = props.selectedFiltersOperator || filtersOperator;
  const hasFirstTimeEventFilter = props.filters.some(filter => isFirstTimeEventFilter(filter));
  const [firstTimeFilters, regularFilters] = partition(props.filters, isFirstTimeEventFilter);
  const hasCohorts = props.sectionProps.projectHasCohorts && props.sectionProps.allowCohortFilter;
  if (props.filters.length === 0 && !showPlaceholder) {
    return null;
  }
  const filterParts = {
    [EntryPart.Filters]: regularFilters,
    [EntryPart.FirstTimeFilters]: firstTimeFilters
  };
  const handleFiltersUpdate = (updates: EntrySectionUpdates<QueryFilterEntry>, source: EntryPart.Filters | EntryPart.FirstTimeFilters): void => {
    const updatedFilters = Object.values(_objectSpread(_objectSpread({}, filterParts), {}, {
      [source]: updates.entries.map(entry => entry.event)
    })).flat();
    props.onUpdate(updatedFilters, selectedFiltersOperator || FiltersOperator.And, updates.queryReason || QueryReason.QbFilterUpdate, updates);
  };
  const filtersEl = (source: EntryPart.Filters | EntryPart.FirstTimeFilters) => {
    var _props$placeholders3, _props$sectionProps$s, _props$placeholders4;
    const filterEntries = getFilterEntriesFromFilters(filterParts[source], selectedFiltersOperator);

    // hide filters if empty or not staging
    if (filterEntries.length === 0 && !((_props$placeholders3 = props.placeholders) !== null && _props$placeholders3 !== void 0 && _props$placeholders3.includes(source))) {
      return null;
    }
    const conjunctionRenderer = _ref => {
      let {
        index: filterIndex,
        entry: filterEntry
      } = _ref;
      return (props.hasNewUI ? filterIndex === 0 && filterEntries.length > 1 : true) && <RowFiltersPrefix filterIndex={filterIndex} filtersOperators={props.filtersOperators} filter={new PropertyFilter((filterEntry as QueryFilterEntry).event)} filtersOperator={selectedFiltersOperator || FiltersOperator.And} isReadOnlyMode={props.sectionProps.readonly} forNewUI={props.hasNewUI} handleChange={item => props.onUpdate(props.filters, item.value, QueryReason.QbFilterUpdate)} />;
    };
    return <QueryEntrySectionReact containedSection indentFilters key={"entry-".concat(source, "-section")} elref={camelCase("entry-".concat(source))} // entryFilters or entryBehavioralFilters, depending on source
    {...props.nestedSectionParams} autoactivatePlaceholders enableAllDataGroupsForScdProps={props.sectionProps.enableAllDataGroupsForScdProps} eventNames={props.eventNames} chartType={props.sectionProps.chartType} dateUnit={props.sectionProps.dateUnit} dataGroupsEnabled={props.sectionProps.dataGroupsEnabled} dataGroupId={props.metricLevelDataGroups ? props.entry.dataGroupId : props.sectionProps.dataGroupId} disableAddEntry canRenameStep={false} behaviorMode={props.sectionProps.behaviorMode} rowIndex={props.index} rowTypes={[SelectorTypeDefinitions.Filter]} rowLabelType={RowLabelType.None} hideEvents={props.hideEvents} projectHasCohorts={hasCohorts} inlineCohortsOverride={props.sectionProps.inlineCohortsOverride} disabledItems={isXauMathType(aggregationOperator) ? Array.from(BEHAVIOR_PROPERTIES.keys()).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}) : {}} rowDraggable={props.hasNewUI} trackProperties={_objectSpread(_objectSpread({}, props.nestedSectionParams.trackProperties || {}), {}, {
      sectionName: "inline-filter"
    })} disableCloneEntry={source === EntryPart.FirstTimeFilters} entries={filterEntries} sectionName={(_props$sectionProps$s = props.sectionProps.sectionName) !== null && _props$sectionProps$s !== void 0 ? _props$sectionProps$s : "inline-filter"}
    // renders the AND/OR switch for inline filters
    renderPrefix={!props.hasNewUI && conjunctionRenderer} renderSuffix={props.hasNewUI && conjunctionRenderer} stagingRow={((_props$placeholders4 = props.placeholders) === null || _props$placeholders4 === void 0 ? void 0 : _props$placeholders4.includes(source)) && _objectSpread(_objectSpread({}, props.placeholders[1]), {}, {
      filtersOperator: selectedFiltersOperator || FiltersOperator.And
    }) as QueryFilterEntry} onUpdate={(updates: EntrySectionUpdates<QueryFilterEntry>) => handleFiltersUpdate(updates, source)} onAbortStage={props.onAbortStage} {...props.sectionParams} data-sentry-element="QueryEntrySectionReact" data-sentry-component="filtersEl" data-sentry-source-file="filters.tsx" />;
  };
  return <div className={clsx("mp-query-entry-row", "for-filters", "has-row-separator", props.extraClass)} data-sentry-component="RowFilters" data-sentry-source-file="filters.tsx">
      {filtersEl(EntryPart.Filters)}
      {hasFirstTimeEventFilter && filtersEl(EntryPart.FirstTimeFilters)}
    </div>;
}
export function RowFiltersPrefix(props: {
  filtersOperator: FiltersOperator;
  filter: PropertyFilter;
  filterIndex: number;
  handleChange: (item: SectionItem) => void;
  forceDisabled?: boolean;
  isReadOnlyMode: boolean;
  forBehaviors?: boolean;
  filtersOperators?: FiltersOperator[];
  forNewUI?: boolean;
}) {
  const {
    t,
    print
  } = useTranslation();
  let canSwitch = false;
  let label = "";
  let operators: FiltersOperator[] = [];
  const forExtendedLogicalOperators = props.filtersOperators && (props.filtersOperators.includes(FiltersOperator.AndAny) || props.filtersOperators.includes(FiltersOperator.OrAll));
  const isExtendedLogicalOperator = props.filtersOperator === FiltersOperator.AndAny || props.filtersOperator === FiltersOperator.OrAll;

  // behavior filter as first filter
  if (props.filter && isFirstTimeEventFilter(props.filter)) {
    label = "for the";
    if (props.forNewUI) {
      return null;
    }
    // first non-behavior filter
  } else if (props.filterIndex === 0 && !props.forNewUI) {
    label = props.forBehaviors ? t("filters.user-operator.who") : t("filters.user-operator.where");
  } else {
    label = print(FiltersOperatorDisplayLabels[props.filtersOperator]);
    canSwitch = true;
    operators = props.filtersOperators || [FiltersOperator.And, FiltersOperator.Or];
  }

  // for sidebar QB we display `And all, Or any`
  let labelMappings = FiltersOperatorDisplayLabels;
  if (props.forNewUI && !forExtendedLogicalOperators) {
    labelMappings = {
      [FiltersOperator.And]: t("filters.group-operator.and-all"),
      [FiltersOperator.Or]: t("filters.group-operator.or-any")
    };
    label = print(labelMappings[props.filtersOperator]);
  }

  // and/or switch
  return <div className={clsx("entry-selector-prefix", {
    'behavior-mode': props.forBehaviors,
    'for-extended-operators': forExtendedLogicalOperators
  })} data-sentry-component="RowFiltersPrefix" data-sentry-source-file="filters.tsx">
      {!(isExtendedLogicalOperator && props.filterIndex > 1) && <SelectReact className={clsx("filters-operator-select", {
      'is-disabled': props.forceDisabled || !canSwitch
    })} readonly={props.isReadOnlyMode || props.forceDisabled || !canSwitch} elref='filtersOperatorSelect' theme={props.forNewUI ? SelectTheme.Secondary : SelectTheme.Tertiary} menuWidth={90} alwaysShowCaret={props.forNewUI} size={props.forNewUI ? ButtonSize.Small : ButtonSize.Medium} sections={[{
      items: operators.map(operator => ({
        isSelected: props.filtersOperator && operator === props.filtersOperator.toString(),
        value: operator,
        label: labelMappings[operator]
      }))
    }]} {...label ? {
      selectedOption: label
    } : {}} onChange={(ev: CustomEvent) => props.handleChange(ev.detail.item as SectionItem)} />}
    </div>;
}